import { useEffect, useState } from "react";
import { Loader2, Mail, Calendar, BookOpen, CheckCircle2, LogIn, NotebookPen, MessageCircle, Shield, Trash2, X } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { adminUsers, adminDeleteUser, formatApiError } from "@/lib/inmindApi";
import { useAuth } from "@/lib/AuthContext";

export default function AdminUsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    adminUsers().then(setUsers).finally(() => setLoading(false));
  }, []);

  const remove = async () => {
    if (!confirmTarget) return;
    setDeleting(true);
    try {
      const res = await adminDeleteUser(confirmTarget.id);
      setUsers((arr) => arr.filter((u) => u.id !== confirmTarget.id));
      toast.success(`${res.email} removed`);
      setConfirmTarget(null);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-24 text-[var(--inmind-muted)]"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>;
  }

  return (
    <div data-testid="admin-users-page">
      <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--inmind-muted)] mb-2">
        All users · {users.length}
      </div>

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {users.map((u) => (
          <Card key={u.id} className="rounded-2xl border border-black/5 bg-white shadow-[0_4px_18px_rgba(0,0,0,0.03)]" data-testid={`user-card-${u.id}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-serif text-xl text-[var(--inmind-text)] truncate">{u.name || u.email}</div>
                  <div className="text-sm text-[var(--inmind-text-secondary)] flex items-center gap-1.5 mt-0.5">
                    <Mail className="w-3 h-3" strokeWidth={1.7} /> {u.email}
                  </div>
                </div>
                {u.role === "admin" && (
                  <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.18em] uppercase font-semibold bg-[var(--inmind-primary)]/10 text-[var(--inmind-primary)] rounded-full px-2.5 py-1">
                    <Shield className="w-3 h-3" strokeWidth={1.8} /> Admin
                  </span>
                )}
              </div>

              <div className="grid grid-cols-2 gap-2 mt-4 text-xs text-[var(--inmind-text-secondary)]">
                <Mini icon={Calendar} label="Joined" value={fmtDate(u.created_at)} />
                <Mini icon={LogIn} label="Logins" value={u.login_count} />
                <Mini icon={BookOpen} label="Seen" value={u.seen_count} />
                <Mini icon={CheckCircle2} label="Reviewed" value={u.reviewed_count} />
                <Mini icon={NotebookPen} label="Notes" value={u.notes_count} />
                <Mini icon={MessageCircle} label="Think msgs" value={u.ai_messages_count} />
              </div>

              {u.auth_providers && u.auth_providers.length > 0 && (
                <div className="mt-4 pt-3 border-t border-black/5 flex flex-wrap gap-2 text-[10px] tracking-[0.18em] uppercase font-semibold">
                  {u.auth_providers.map((p) => <Tag key={p}>{p}</Tag>)}
                  {u.preferences?.timezone && <Tag>{u.preferences.timezone}</Tag>}
                  {u.preferences?.notify_time && <Tag>{u.preferences.notify_time}</Tag>}
                </div>
              )}

              {u.id !== me?.id && (
                <button
                  onClick={() => setConfirmTarget(u)}
                  data-testid={`delete-user-${u.id}`}
                  className="mt-4 inline-flex items-center gap-1.5 text-xs text-red-600 hover:text-red-700 hover:underline transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" strokeWidth={1.7} /> Delete account
                </button>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Confirm modal */}
      {confirmTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-6 bg-black/30 backdrop-blur-sm"
          data-testid="delete-confirm-modal"
          onClick={() => !deleting && setConfirmTarget(null)}
        >
          <div
            className="bg-white rounded-2xl border border-black/5 shadow-2xl max-w-sm w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center shrink-0">
                <Trash2 className="w-4 h-4 text-red-600" strokeWidth={1.8} />
              </div>
              <div className="min-w-0">
                <div className="font-serif text-xl text-[var(--inmind-text)]">Delete this user?</div>
                <p className="text-sm text-[var(--inmind-text-secondary)] mt-1">
                  Removes <span className="font-medium text-[var(--inmind-text)]">{confirmTarget.email}</span> and all their notes, AI chats, sessions and login history. This cannot be undone.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <Button
                variant="ghost"
                onClick={() => setConfirmTarget(null)}
                disabled={deleting}
                data-testid="delete-cancel"
                className="rounded-xl h-10 px-4 hover:bg-black/5"
              >
                <X className="w-3.5 h-3.5" strokeWidth={1.8} /> Cancel
              </Button>
              <Button
                onClick={remove}
                disabled={deleting}
                data-testid="delete-confirm"
                className="rounded-xl h-10 px-4 bg-red-600 text-white hover:bg-red-700 transition-colors"
              >
                {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" strokeWidth={1.8} />}
                Delete user
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Mini({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-1.5">
      <Icon className="w-3 h-3 text-[var(--inmind-muted)]" strokeWidth={1.7} />
      <span className="text-[10px] tracking-[0.18em] uppercase font-semibold text-[var(--inmind-muted)]">{label}</span>
      <span className="ml-auto text-[var(--inmind-text)] font-medium tabular-nums">{value}</span>
    </div>
  );
}

function Tag({ children }) {
  return (
    <span className="rounded-full bg-black/5 text-[var(--inmind-text-secondary)] px-2 py-0.5">{children}</span>
  );
}

function fmtDate(s) {
  if (!s) return "—";
  return new Date(s).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}
